import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  getCurrentEnvForApi,
  setCurrentEnvForApi,
} from '../api/apiBaseUrl';
import { colors } from '../theme/tokens';
import { useCurrentUser } from '../auth/useCurrentUser';
import { useVisibleLadderEnvs } from './useVisibleLadderEnvs';

/**
 * WU11 — Environment Promotion UI.
 *
 * Tracks which promotion-ladder env the UI is pointed at. The "editable env"
 * is the single env where mutations are legal (typically `dev01`); every
 * other env is read-only — the UI must hide write affordances and the
 * ReadOnlyBanner must be visible.
 *
 * Env list and editable env: with the per-tenant editing-env feature ON they
 * come from the RUNTIME /api/app-env (ladderEnv = this deployment's home/write
 * env; remoteReadableEnvs = the browsable peers) — because the bundle is shared
 * across envs and build-time REACT_APP_* can't vary per deploy. With the flag
 * OFF they fall back to the legacy build-time vars:
 *   REACT_APP_AVAILABLE_ENVS = "dev01,qa,uat,prod"
 *   REACT_APP_EDITABLE_ENV   = "dev01"
 *
 * Persistence: `client-portal:currentEnv` in localStorage. Cross-tab
 * synchronization rides the `storage` event so opening a second tab
 * inherits the selection. If the persisted value isn't in availableEnvs
 * (config drift after a release that removed an env), we drop it on mount.
 */


/** Per-env metadata shown in the dropdown. */
export interface EnvDescriptor {
  name: string;
  /** True iff this env equals `editableEnv` — only env where writes are allowed. */
  isEditable: boolean;
  /** True iff this env is production (drives the red badge + extra confirmations). */
  isProd: boolean;
  /** Hex/token color used by the badge in the dropdown. */
  badgeColor: string;
}

export interface EnvContextValue {
  currentEnv: string;
  setCurrentEnv: (env: string) => void;
  /** True iff `currentEnv === editableEnv`. Shorthand for `useIsEditable`. */
  isEditable: boolean;
  /** The single env where mutations are allowed. */
  editableEnv: string;
  availableEnvs: EnvDescriptor[];
}

const EnvContext = createContext<EnvContextValue | null>(null);

/**
 * Pure helpers extracted so tests can exercise them without rendering.
 */
const parseAvailableEnvs = (raw: string): string[] =>
  raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

const badgeColorFor = (name: string): string => {
  if (name === 'dev01') return colors.envDev;
  if (name === 'prod') return colors.envProd;
  return colors.infoBgStrong;
};

const buildDescriptors = (names: string[], editable: string): EnvDescriptor[] =>
  names.map((name) => ({
    name,
    isEditable: name === editable,
    isProd: name === 'prod',
    badgeColor: badgeColorFor(name),
  }));

export const EnvProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // dev01 is the single authoring env on every domain (the per-tenant / per-
  // deploy editing-env model is retired). We no longer consult /api/app-env's
  // ladderEnv to pick the editable env or the default — every deployment's
  // bundle treats dev01 as home and edits it browser-direct, so the qa/uat/etc.
  // portals are NOT read-only just because they served the bundle.

  // Build-time fallbacks: used wholesale when the flag is OFF, and as the
  // pre-app-env BOOT fallback when ON (reconciled to runtime by the effect
  // below once /api/app-env resolves).
  const buildEditableEnv = process.env.REACT_APP_EDITABLE_ENV ?? 'dev01';
  const buildAvailableNames = useMemo(
    () => parseAvailableEnvs(process.env.REACT_APP_AVAILABLE_ENVS ?? 'dev01,qa,uat,integ,prod'),
    [],
  );

  // HOME = the single authoring/editable env, and it is ALWAYS the build-time
  // editable env (dev01) on EVERY domain. We deliberately do NOT derive it from
  // the deploy's ladderEnv: the per-tenant / per-deploy editing-env model is
  // retired, so the qa/uat/etc. portals must still edit dev01 (browser-direct)
  // instead of flipping the whole app to read-only just because the bundle was
  // served from dosa.qa.
  const editableEnv = buildEditableEnv;
  // Dropdown always offers the full env list (dev01 first) regardless of which
  // domain served the bundle, so dev01 is selectable + default everywhere —
  // MINUS 'prod' when RBAC_V2 is on and the user lacks `prod_option` (decisions
  // §8 — "PROD disappears from env dropdowns"). Flag off: never filtered.
  const availableEnvNames = useVisibleLadderEnvs(buildAvailableNames);
  // Whether the /api/me query (which `prod_option` gating ultimately reads,
  // via useVisibleLadderEnvs → useCurrentUser) has resolved — the reconcile
  // effect below needs this so it never acts on the loading-window default.
  const { isLoading: meLoading } = useCurrentUser();
  const availableEnvs = useMemo(
    () => buildDescriptors(availableEnvNames, editableEnv),
    [availableEnvNames, editableEnv],
  );

  // No persistence: dev01 (editableEnv) is the default on every domain and every
  // tab. We intentionally do NOT restore a previously-selected env — the env is
  // never saved anywhere, so a fresh load always starts on dev01.
  const initialEnv = editableEnv;

  const [currentEnv, setCurrentEnvState] = useState<string>(initialEnv);

  // Write the SELECTED env to the API baseURL ref synchronously during render
  // (NOT in a useEffect — that runs after children render, so a child firing a
  // request in its own effect would see a stale baseURL). It MUST use
  // `currentEnv`, not the constant `initialEnv`: otherwise a re-render after the
  // user switches envs would reset the ref back to dev01 and silently send their
  // reads to the wrong env. Idempotent module-level state we control.
  setCurrentEnvForApi(currentEnv);

  // Env is never persisted, so there is no cross-tab storage sync — each tab
  // independently starts on dev01. Keep a ref to the current env only for the
  // no-op guard in setCurrentEnv.
  const currentEnvRef = useRef(currentEnv);
  currentEnvRef.current = currentEnv;

  const queryClient = useQueryClient();

  const setCurrentEnv = useCallback(
    (env: string) => {
      // No-op on identical value — prevents storage churn + query cache
      // invalidations on repeat clicks of the same option.
      if (getCurrentEnvForApi() === env && currentEnvRef.current === env) return;
      if (!availableEnvNames.includes(env)) {
        // Defensive: ignore values not in the list (e.g. stale dropdown
        // state after a release that removed an env). UI should never offer
        // an invalid value, but guard anyway.
        return;
      }
      setCurrentEnvForApi(env);
      setCurrentEnvState(env);
      // Every cached query was fetched against the OLD env's backend. The
      // axios interceptor now resolves to a different baseURL, but React
      // Query won't refetch on its own — the cache is keyed by query-key,
      // not by network URL. Bust the entire cache so every page refetches
      // against the new env. Coarse but correct for an admin tool.
      queryClient.invalidateQueries();
    },
    [availableEnvNames, queryClient],
  );

  // No env persistence: the selected env always STARTS at dev01 and only
  // changes when the user picks another from the dropdown (that choice is
  // not saved). dev01 is the single editable env on every domain.

  // Prod-option reconcile (decisions §8, P1 fix): `availableEnvNames` can
  // drop `'prod'` out from under an ALREADY-selected env — a grant revoke,
  // or the /api/me query resolving after mount with the flag on and no
  // `prod_option` held. If the currently-selected env falls out of the
  // list, fall back to the editable env rather than leaving `currentEnv`
  // pointing at an env the UI no longer offers. Gated on the /api/me query
  // having resolved (`meLoading`) and the list being non-empty so this
  // never fires on the loading-window default or a transient empty list —
  // and only ACTS on a genuine mismatch, so it can't loop (once corrected,
  // `currentEnv` is always the editable env, which is always in the list).
  useEffect(() => {
    if (meLoading || availableEnvNames.length === 0) return;
    if (!availableEnvNames.includes(currentEnv)) {
      setCurrentEnv(editableEnv);
    }
  }, [meLoading, availableEnvNames, currentEnv, editableEnv, setCurrentEnv]);

  const value = useMemo<EnvContextValue>(
    () => ({
      currentEnv,
      setCurrentEnv,
      // dev01 is the only editable env, on every domain. Any other SELECTED env
      // is a read-only view (browse remote envs; author on dev01).
      isEditable: currentEnv === editableEnv,
      editableEnv,
      availableEnvs,
    }),
    [currentEnv, setCurrentEnv, editableEnv, availableEnvs],
  );

  return <EnvContext.Provider value={value}>{children}</EnvContext.Provider>;
};

/**
 * Hook to read/write the active env. Throws outside an EnvProvider — same
 * pattern as `useTenant` — so misuse surfaces immediately rather than as
 * mysterious "currentEnv is undefined" errors.
 */
export const useEnv = (): EnvContextValue => {
  const ctx = useContext(EnvContext);
  if (ctx === null) {
    throw new Error('useEnv must be used within an EnvProvider');
  }
  return ctx;
};

/**
 * Non-throwing variant for LEAF UI (e.g. the shared write-affordance gate) that
 * may render outside an EnvProvider — the tenant picker, or a unit test that
 * mounts a single component. Returns null when there is no provider; the
 * editability hooks treat that as "outside the app shell → transparent" (the
 * server write-fence is the real guarantee). Real env-scoped code keeps using
 * useEnv() so a genuinely-missing provider still surfaces loudly there.
 */
export const useEnvOptional = (): EnvContextValue | null =>
  useContext(EnvContext);

// Test-only exports — not part of the public surface.
export const __test = {
  parseAvailableEnvs,
  badgeColorFor,
  buildDescriptors,
};
